import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { arrayAdd, arrayRemove, ID } from '@datorama/akita';
import { tap } from 'rxjs/operators';
import { Column, TableCollectionTemplate } from './base-templates/table/table.model';
import { Collection, LayoutLocationCode } from './collection.model';
import { CollectionQuery } from './collection.query';
import { CollectionStore } from './collection.store';
import { Filter } from "../filter/filter.model";
import { Subscription } from "rxjs";

@Injectable({ providedIn: 'root' })
export class CollectionService {
  public currentPage: number;
  public currentLimit: number;

	constructor(private collectionQuery: CollectionQuery, private collectionStore: CollectionStore, private http: HttpClient) {}

	add(collection: Collection) {
		this.collectionStore.add(collection);
	}

	update(id: Collection['id'], collection: Partial<Collection>) {
		this.collectionStore.update(id, collection);
	}

	upsert(id: Collection['id'], collection: Partial<Collection>) {
		this.collectionStore.upsert(id, collection);
	}

	remove(id: Collection['id']) {
		this.collectionStore.remove(id);
	}

	setEntities(id: Collection['id'], entities: any) {
		console.log('📦 CollectionService.setEntities:', id, 'First entity:', entities[0] ? { name: entities[0].name, isPinned: entities[0].isPinned } : 'No entities');
		this.collectionStore.update(id, { entities });
	}

	addEntities(id: Collection['id'], entitiesToAdd: any) {
    const entities = (this.collectionQuery.getEntity(id)?.entities || []).concat(entitiesToAdd)
		this.collectionStore.update(id, { entities });
	}

  updateEntity(id: Collection['id'], updatedEntity: any) {
    const entities = [...this.collectionQuery.getEntity(id)?.entities];
    const index = entities.findIndex(e => e.id === updatedEntity.id);
    if (index > -1) {
      entities[index] = { ...entities[index], ...updatedEntity };
    } else {
      entities.push(updatedEntity)
    }
    this.collectionStore.update(id, { entities });
  }

	removeEntities(id: Collection['id'], entities: any) {
    const collection = this.collectionQuery.getEntity(id);

		this.collectionStore.update(
      id,
      {
        entities: collection?.entities.filter(e => !entities.includes(e.id)),
        // totalResults: collection?.totalResults - 1,

      }
    );
	}

	updateQueryParams(id: Collection['id'], queryParams: Partial<Collection['queryParams']>) {
		this.collectionStore.update(id, {
			queryParams: {
				...this.collectionQuery.getEntity(id)?.queryParams,
				...queryParams
			}
		});
	}

	// update a specific filter
	updateFilter(id: Collection['id'], filterId: Filter['id'], filter: Partial<Filter>) {
		this.collectionStore.update(id, {
			filters: [
				...this.collectionQuery.getEntity(id)?.filters.map(f => {
					if (f.id === filterId) {
						return {
							...f,
							...filter
						}
					}
					return f;
				})
			]
		});
	}

	// remove a specific filter
	removeFilter(id: Collection['id'], filterId: Filter['id']) {
		this.collectionStore.update(id, {
			filters: [...this.collectionQuery.getEntity(id)?.filters.filter(f => f.id !== filterId)]
		});
	}

	toggleSelectForEntities(id: Collection['id'], entities: any[]) {
		let selectedEntities = this.collectionQuery.getEntity(id)?.selectedEntities;

		entities.forEach(entity => {
			if (selectedEntities.map(e => e.id).includes(entity.id)) {
				selectedEntities = arrayRemove(selectedEntities, entity.id);
			} else {
				selectedEntities = arrayAdd(selectedEntities, entity);
			}
		});

		this.collectionStore.update(id, { selectedEntities });
	}

	setLoadForEntities(id: Collection['id'], entities: any, loading: boolean) {
		let loadingEntities = this.collectionQuery.getEntity(id)?.loadingEntities;

		entities.forEach(entity => {
			if (loading) {
				loadingEntities = arrayAdd(loadingEntities, entity);
			} else {
				loadingEntities = arrayRemove(loadingEntities, entity.id);
			}
		});

		this.collectionStore.update(id, { loadingEntities });
	}

	clearSelectedEntities(id: Collection['id']) {
		this.collectionStore.update(id, { selectedEntities: [] });
	}

	setActiveColumns(id: Collection['id'], columns: Column[]) {
		const currentTemplateFromStore = this.collectionQuery.getEntity(id).currentTemplate;
		this.collectionStore.update(id as any, {
			currentTemplate: {
				...currentTemplateFromStore,
				settings: {
					...currentTemplateFromStore.settings,
					activeColumns: columns
				}
			}
		});
	}

	addAdditionalColumns(id: Collection['id'], columns: Column[]) {
		const currentTemplateFromStore = this.collectionQuery.getEntity(id).currentTemplate;
		const updatedColumns = [ ...currentTemplateFromStore.settings.columns ];

		for (const column of columns) {
			const found = currentTemplateFromStore.settings.columns.find(item => item.id === column.id);

			if (!found) {
				updatedColumns.push(column);
			}
		}

		this.collectionStore.update(id as any, {
			currentTemplate: {
				...currentTemplateFromStore,
				settings: {
					...currentTemplateFromStore.settings,
					columns: updatedColumns
				}
			}
		});
	}

	toggleSidebar(id: Collection['id']) {
		this.collectionStore.update(id, { sidebarOpen: !this.collectionQuery.getEntity(id).sidebarOpen });
	}

  updateByLocationCode(locationCodes: LayoutLocationCode[], entities: any) {
    const collections = this.collectionStore.getValue().entities

    for (const id in collections) {
      if (Object.prototype.hasOwnProperty.call(collections, id)) {
        if ( locationCodes.includes(collections[id].layoutLocationCode)) {
          this.addEntities(id, entities);
        }
      }
    }
  }

	exportCollectionEntities(id: string, entities: any, service: any, resultMethodName: string, setLoad: boolean) {
		if (setLoad) {
			this.setLoadForEntities(id, entities, true);
		}

		entities.forEach((entity: any) => {
			const subscription = service[resultMethodName](entity) as Subscription;

			subscription.add(() => {
				if (setLoad) {
					this.setLoadForEntities(id, [entity], false);
				}
			});
		});
	}
}
